import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Observable, catchError, from, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { saveAs } from 'file-saver'

import { AnnexBureauResult, SnapshotContext, SnapshotFileKind, SnapshotResponse, SnapshotTab, SnapshotView } from '@data/interfaces/verification-snapshot.interface'
import { fileNameFromContentDisposition } from '@data/utils/content-disposition.util'
import { environment } from '@env/environment'

const CONNECTION_ERROR = 'Hubo un error de conexión'

/** El PDF de la foto, listo para el visor: el archivo y el nombre de `Content-Disposition`. */
export interface SnapshotFileContent {
  blob: Blob
  fileName: string
}

/**
 * La foto de Syntage (F-158).
 *
 *   GET  /leases/:id/verification-snapshot?tab=     · vista de la pestaña sobre la revisión del arrendamiento
 *   GET  /clients/:id/verification-snapshot?tab=    · vista sobre la verificación vigente del cliente
 *   POST /{leases|clients}/:id/verification-snapshot/bureau · anexo tardío de Buró y BIL (solo lectura en Syntage)
 *   GET  /verification-snapshots/:snapshotId/files/:kind[?revision=N] · PDF por proxy (nunca expone el blob de Syntage)
 *
 * Los cuatro exigen `leases.view` (o `leases.update` el anexo) en los dos contextos: el permiso es el
 * de la pantalla, no el del recurso — con `clients.view` el Buró llegaría a Broker y MasterBroker.
 *
 * **No extiende `ApiClass`**, igual que `ApiCommitteeService`: cada método arma su URL. Contrato L de
 * `standards.md`: ningún método lanza; el error viaja en el sobre con `status` y `code`, que es lo que
 * la pestaña usa para distinguir «sin permiso» de «falló la red».
 */
@Injectable({ providedIn: 'root' })
export class ApiVerificationSnapshotService {
  private readonly http = inject(HttpClient)

  getView(ctx: SnapshotContext, tab: SnapshotTab): Observable<SnapshotResponse<SnapshotView>> {
    return this.wrap(this.http.get(this.contextUrl(ctx), { params: { tab } }), 'No pudimos cargar esta sección.')
  }

  annexBureau(ctx: SnapshotContext): Observable<SnapshotResponse<AnnexBureauResult>> {
    return this.wrap(this.http.post(`${this.contextUrl(ctx)}/bureau`, {}), 'No se pudo consultar Syntage')
  }

  /**
   * Trae el PDF para abrirlo en el previsualizador, con el nombre que manda el backend (`Buro-<RFC>-<AAAA-MM-DD>.pdf`).
   * `revision` es la que se está viendo (`snapshot.viewedRevision`): un arrendamiento congelado ve el PDF que vio y
   * no el de un anexo posterior. Sin ella el backend entrega el de la última revisión. No guarda nada: guardar es
   * «Descargar» dentro del visor (`saveBlob`).
   */
  fetchFile(snapshotId: string, kind: SnapshotFileKind, revision?: number | null): Observable<SnapshotResponse<SnapshotFileContent>> {
    const params: Record<string, string> = revision ? { revision: String(revision) } : {}
    return this.http.get(`${environment.apiUrl}verification-snapshots/${snapshotId}/files/${kind}`, { params, responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>): SnapshotResponse<SnapshotFileContent> => {
        if (!response.body) return { error: true, msg: 'El servidor no entregó el PDF', data: null, status: response.status }
        const fileName = fileNameFromContentDisposition(response.headers.get('content-disposition')) ?? `${kind}.pdf`
        return { error: false, msg: '', data: { blob: response.body, fileName }, status: response.status }
      }),
      catchError((e: HttpErrorResponse) => this.blobErrorBody(e).pipe(map((body) => this.toError<SnapshotFileContent>(e, 'No se pudo abrir el PDF', body)))),
    )
  }

  /**
   * Con `responseType: 'blob'` el cuerpo de un error también llega como Blob: se lee como JSON para no perder el
   * `code` (`FILE_NOT_AVAILABLE`) con el que la pestaña decide qué decir. Un cuerpo que no es JSON queda en `null`.
   */
  private blobErrorBody(e: HttpErrorResponse): Observable<any> {
    if (!(e?.error instanceof Blob)) return of(e?.error)
    return from(e.error.text()).pipe(
      map((text) => JSON.parse(text)),
      catchError(() => of(null)),
    )
  }

  /** Guarda el PDF que ya se ve en el visor. Aislado para poder espiarlo en las pruebas: `file-saver` toca el DOM. */
  saveBlob(blob: Blob, fileName: string): void {
    saveAs(blob, fileName)
  }

  private contextUrl(ctx: SnapshotContext): string {
    const resource = ctx.scope === 'lease' ? 'leases' : 'clients'
    return `${environment.apiUrl}${resource}/${ctx.id}/verification-snapshot`
  }

  private wrap<T>(source: Observable<unknown>, fallback: string): Observable<SnapshotResponse<T>> {
    return source.pipe(
      map((r: any) => ({ error: !!r?.error, msg: r?.msg ?? '', data: (r?.data ?? null) as T | null, status: 200 })),
      catchError((e: HttpErrorResponse) => of(this.toError<T>(e, fallback))),
    )
  }

  //* El 409 del anexo trae el motivo de negocio en `message` (Nest) o en `msg` (filtro del panel); se
  //* propaga tal cual porque el toast lo muestra al usuario. El `code` del backend manda también en un 403:
  //* `FORBIDDEN` es solo el respaldo cuando no trae uno propio.
  private toError<T>(e: HttpErrorResponse, fallback: string, body: any = e?.error instanceof Blob ? null : e?.error): SnapshotResponse<T> {
    const status = e?.status ?? 0
    if (status === 0) return { error: true, msg: CONNECTION_ERROR, data: null, status, code: 'NETWORK' }
    const code = body?.code ?? (status === 403 ? 'FORBIDDEN' : undefined)
    return { error: true, msg: body?.message ?? body?.msg ?? fallback, data: null, status, code }
  }
}
